import { useEffect, useState } from "react";
import { Lock, Users, HeartHandshake, Gift, HandHelping, Image as ImageIcon, Video, Mic2, Sparkles, Clock, Send } from "lucide-react";
import { portalApi, mediaUrl } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import { BADGE_ICONS, ENCOURAGEMENT_MAP } from "./badgeIcons";
import MediaGrid from "./MediaGrid";
import VoicePlayer from "./VoicePlayer";

const TABS = [
  { key: "private_reflections", label: "Private Reflections", icon: Lock },
  { key: "witness_journey", label: "Witness Journey", icon: HeartHandshake },
  { key: "community_posts", label: "Community Posts", icon: Users },
  { key: "encouragement_received", label: "Encouragement Received", icon: Gift },
  { key: "encouragement_given", label: "Encouragement Given", icon: Send },
  { key: "prayer_journal", label: "Prayer Journal", icon: HandHelping },
  { key: "photos", label: "Photos", icon: ImageIcon },
  { key: "videos", label: "Videos", icon: Video },
  { key: "voices", label: "Voice Notes", icon: Mic2 },
  { key: "timeline", label: "Journey Timeline", icon: Clock },
  { key: "milestones", label: "Milestones", icon: Sparkles },
];

function EntryCard({ e }) {
  return (
    <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-5">
      {e.date && <p className="text-[10px] tracking-widest uppercase text-[#A9724E] mb-1.5">{e.date}</p>}
      {e.text && <p className="text-[#2A2825] font-body leading-relaxed whitespace-pre-wrap">{e.text}</p>}
      <MediaGrid media={e.media} />
    </div>
  );
}

export default function MyVessel() {
  const { user } = usePortalAuth();
  const [tab, setTab] = useState("private_reflections");
  const [v, setV] = useState(null);

  useEffect(() => { portalApi.get("/portal/vessel").then((r) => setV(r.data)).catch(() => {}); }, []);

  const Empty = () => <p className="text-[#8a7a6a] font-body text-sm">Nothing here yet — it fills as you pour out.</p>;

  const render = () => {
    if (!v) return <p className="text-[#8a7a6a] font-body">Gathering…</p>;
    const list = v[tab] || [];
    if (["private_reflections", "witness_journey", "community_posts"].includes(tab))
      return list.length ? <div className="space-y-4" data-testid={`vessel-list-${tab}`}>{list.map((e) => <EntryCard key={e.id} e={e} />)}</div> : <Empty />;
    if (tab === "prayer_journal")
      return list.length ? <div className="space-y-3">{list.map((p, i) => <div key={i} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-5"><p className="text-[10px] tracking-widest uppercase text-[#A9724E] mb-1.5">{p.date}</p><p className="font-body text-[#2A2825] whitespace-pre-wrap">{p.text}</p></div>)}</div> : <Empty />;
    if (tab === "photos" || tab === "videos")
      return list.length ? <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{list.map((m, i) => <div key={i} className="rounded-lg overflow-hidden border border-[#E0D8C9] bg-black/5">{tab === "videos" ? <video src={mediaUrl(m.path)} controls className="w-full aspect-square object-cover bg-black" /> : <img src={mediaUrl(m.path)} alt="" className="w-full aspect-square object-cover" />}</div>)}</div> : <Empty />;
    if (tab === "voices")
      return list.length ? <div className="space-y-3">{list.map((m, i) => <div key={i} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-4"><p className="text-[10px] tracking-widest uppercase text-[#A9724E] mb-1">{m.date}</p><VoicePlayer path={m.path} /></div>)}</div> : <Empty />;
    if (tab === "encouragement_received")
      return list.length ? <div className="space-y-3">{list.map((c) => { const e = ENCOURAGEMENT_MAP[c.kind]; return <div key={c.id} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-5"><p className="text-sm"><span className="text-[#A9724E] font-medium">{c.from_name}</span>{e && <span className="ml-2 text-[10px] tracking-widest uppercase text-[#8a7a6a]">{e.label}</span>}</p>{c.text && <p className="text-[#2A2825] font-body mt-1 whitespace-pre-wrap">{c.text}</p>}</div>; })}</div> : <Empty />;
    if (tab === "encouragement_given")
      return list.length ? <div className="space-y-3">{list.map((c) => { const e = ENCOURAGEMENT_MAP[c.kind]; return <div key={c.id} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-5">{e && <span className="text-[10px] tracking-widest uppercase text-[#8a7a6a]">{e.label}</span>}{c.text && <p className="text-[#2A2825] font-body mt-1 whitespace-pre-wrap">{c.text}</p>}</div>; })}</div> : <Empty />;
    if (tab === "timeline")
      return list.length ? <div className="space-y-2">{list.map((t, i) => <div key={i} className="flex items-center gap-3 text-sm font-body"><span className="w-2 h-2 rounded-full bg-[#A9724E]" /><span className="text-[#5E4B3C]">Week {t.week}</span><span className="text-[#8a7a6a]">· {t.type} · {t.date}</span></div>)}</div> : <Empty />;
    if (tab === "milestones") {
      const badges = v.badges || [];
      return <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">{badges.map((b) => { const Icon = BADGE_ICONS[b.icon] || Sparkles; return <div key={b.key} title={b.desc} className={`rounded-xl border p-3 text-center ${b.earned ? "bg-[#EAE3D9] border-[#D8CEBE]" : "bg-white/40 border-[#E0D8C9] opacity-45"}`}><Icon className={`w-6 h-6 mx-auto mb-1.5 ${b.earned ? "text-[#A9724E]" : "text-[#b8ad9c]"}`} strokeWidth={1.5} /><p className="font-serif-display text-xs leading-tight">{b.name}</p></div>; })}</div>;
    }
    return <Empty />;
  };

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div data-testid="portal-vessel" className="max-w-3xl">
      <p className="tracking-widest text-xs uppercase text-[#A9724E] mb-3">Your permanent testimony</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">My Vessel</h1>
      <p className="text-[#5E4B3C] font-body mb-8 leading-relaxed">Everything you pour out gathers here, quietly and automatically{firstName ? `, ${firstName}` : ""}. Nothing is ever lost.</p>

      <div className="flex flex-wrap gap-2 mb-8">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`vessel-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs transition-colors ${tab === t.key ? "bg-[#A9724E] text-[#F4EFE6]" : "bg-[#EAE3D9] text-[#5E4B3C] hover:bg-[#e0d8c9]"}`}>
            <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />{t.label}
          </button>
        ))}
      </div>

      <div data-testid={`vessel-panel-${tab}`}>{render()}</div>
    </div>
  );
}
